import isEqual from 'lodash/isEqual'
import { ProjectStatuses, ProjectType, ProjectTypes } from '../../common/constants'
import { ChangeSet } from '../../components/Items/itemModel'
import { LegacyJobData, LegacyWorkareaData, UpdateJobItemsChangeSet, UpdateJobRequest } from '../../domain/jobs/jobs'
import { UpdateJobDetailsRequest } from '../../components/jobs/jobsApi'
import { JobAnalysis } from './jobDetailModel'

export type SaveJobOptions = {
  workareas?: LegacyWorkareaData[]
  visits?: unknown[]
  receipts?: unknown[]
  itemsChangeSet?: UpdateJobItemsChangeSet | null
}

/**
 * Legacy sent the whole ProjectDTO and blanked workareas/bundles/visits so the
 * server would leave those alone; only the receipts flow posts them back.
 * This builds exactly the fields UpdateJobDto reads, in that same shape.
 */
export const buildUpdateJobRequest = (job: LegacyJobData, options: SaveJobOptions = {}): UpdateJobRequest => {
  const workareas = [...(options.workareas ?? [])].sort((a, b) => a.sortIndex - b.sortIndex)

  return {
    id: job.id,
    type: job.type,
    no: job.no,
    name: job.name,
    estimateDate: job.estimateDate,
    soldDate: job.soldDate ?? null,
    completedDate: job.completedDate ?? null,
    customerId: job.customerId,
    propertyId: job.propertyId,
    budgetYear: job.budgetYear,
    divisionId: job.divisionId ?? null,
    consultId: job.consultId ?? null,
    salesPersonEmail: job.salesPersonEmail ?? null,
    estimatorEmail: job.estimatorEmail ?? null,
    schedulers: normalizeSchedulers(job.schedulers),
    schedulingNotes: job.schedulingNotes ?? null,
    sold: job.sold,
    lost: job.lost,
    active: job.active,
    status: job.status ?? null,
    notifications: job.notifications,
    clientNotes: job.clientNotes ?? null,
    crewNotes: job.crewNotes ?? null,
    timecardInternalNotesInputMode: job.timecardInternalNotesInputMode,
    timecardClientNotesInputMode: job.timecardClientNotesInputMode,
    workareas,
    visits: options.visits ?? [],
    receipts: options.receipts ?? [],
    itemsChangeSet: options.itemsChangeSet ?? null
  }
}

const normalizeSchedulers = (schedulers: string | null | undefined): string | null => {
  const trimmed = (schedulers ?? '').trim()
  return trimmed === '' ? null : trimmed
}

export type SaveValidationResult = {
  isValid: boolean
  message?: string
}

export const validateJobForSave = (job: LegacyJobData): SaveValidationResult => {
  if (job.sold && !normalizeSchedulers(job.schedulers)) {
    return {
      isValid: false,
      message: `Before marking this ${jobTypeLabel(job.type)} as sold, please select at least one scheduler to notify.`
    }
  }

  if (job.type === ProjectTypes.Maintenance) {
    const tasks = job.workareas.filter(x => !x.removed)

    if (tasks.some(x => !x.name || x.name.trim() === '')) {
      return { isValid: false, message: 'Please enter a name for all tasks' }
    }

    if (tasks.some(x => !x.costCodeId || x.costCodeId <= 0)) {
      return { isValid: false, message: 'Please select a cost code for all tasks' }
    }
  }

  return { isValid: true }
}

const jobTypeLabel = (jobType: ProjectType): string => {
  return {
    [ProjectTypes.Project]: 'project',
    [ProjectTypes.Ticket]: 'service ticket',
    [ProjectTypes.Maintenance]: 'maintenance ticket'
  }[jobType]
}

export const hasUnsavedJobChanges = (job: LegacyJobData, savedJob: LegacyJobData | undefined): boolean => {
  if (!savedJob) {
    return false
  }
  return !isEqual(buildUpdateJobRequest(job), buildUpdateJobRequest(savedJob))
}

const isEstimatedItem = (item: any) => item.isAdditional === undefined

export const buildItemsChangeSetForSave = (
  changeSet: ChangeSet | undefined,
  workareas: LegacyWorkareaData[]
): UpdateJobItemsChangeSet => {
  if (!changeSet) {
    return {
      addedEstimatedItems: [],
      addedActualItems: [],
      updatedEstimatedItems: [],
      updatedActualItems: [],
      deletedItems: []
    }
  }

  const isWorkareaValid = (item: any) => {
    const workarea = workareas.find(x => x.id === item.workareaId)
    return !!workarea && !workarea.removed
  }

  const notDeleted = (item: any) => !changeSet.deletedItems.includes(item.guid)

  return {
    addedEstimatedItems: changeSet.addedItems.filter(x => isEstimatedItem(x) && notDeleted(x) && isWorkareaValid(x)),
    addedActualItems: changeSet.addedItems.filter(x => !isEstimatedItem(x) && notDeleted(x)),
    updatedEstimatedItems: changeSet.updatedItems.filter(x => isEstimatedItem(x)),
    updatedActualItems: changeSet.updatedItems.filter(x => !isEstimatedItem(x)),
    deletedItems: changeSet.deletedItems
  }
}

/**
 * When a background refetch of the job resolves while the user has unsaved edits
 * — an attachment upload, a visit change, an invoicing change all refetch the
 * job — replacing the whole local job would silently discard those edits. Take
 * only the server-owned display fields (attachment count, hero photo) the
 * refetch is meant to refresh and keep everything the user might be editing.
 */
export const mergeRefetchedJob = (current: LegacyJobData, server: LegacyJobData): LegacyJobData => ({
  ...current,
  attachments: server.attachments,
  primaryPhotoUrl: server.primaryPhotoUrl
})

const soldDateForSavedDetails = (current: string | null | undefined, saved: UpdateJobDetailsRequest): string | null => {
  if (!saved.sold || !saved.soldOn) {
    return saved.sold ? current ?? null : null
  }
  return current?.startsWith(saved.soldOn) ? current : `${saved.soldOn}T12:00:00`
}

/**
 * JobDetailsForm autosaves its own subset through /web/jobs/{id}/details. The
 * page has to mirror those values or the next /jobs save would post the stale
 * ones back over them.
 */
export const applySavedJobDetails = (
  job: LegacyJobData,
  saved: UpdateJobDetailsRequest
): LegacyJobData => {
  const statusChanged = (saved.status ?? null) !== (job.status ?? null)

  return {
    ...job,
    no: saved.no || job.no,
    name: saved.name,
    budgetYear: saved.budgetYear,
    salesPersonEmail: saved.salesPersonEmail,
    estimatorEmail: saved.estimatorEmail,
    schedulers: saved.schedulers,
    schedulingNotes: saved.schedulingNotes,
    crewNotes: saved.crewNotes,
    sold: saved.sold,
    soldDate: soldDateForSavedDetails(job.soldDate, saved),
    lost: saved.lost,
    active: saved.active,
    status: saved.status,
    estimateDate: saved.estimateDate,
    completedDate: statusChanged ? completedDateForStatus(saved.status, job.completedDate) : job.completedDate
  }
}

/** Mirrors jobHelper.onProjectStatusChange: completing a job stamps today, anything else clears it. */
export const completedDateForStatus = (
  status: number | null | undefined,
  currentCompletedDate: string | null | undefined
): string | null => {
  if (status === ProjectStatuses.Completed) {
    return currentCompletedDate ?? new Date().toISOString()
  }
  return null
}

export type IntercomEvent = {
  name: string
  metadata: Record<string, unknown>
}

const centsToDisplay = (cents: number | undefined) => ((cents ?? 0) / 100).toFixed(2)

const statusNames = ['Unscheduled', 'Scheduled', 'Waiting', 'Completed']

export const buildJobSaveIntercomEvents = (
  job: LegacyJobData,
  savedJob: LegacyJobData | undefined,
  analysis: JobAnalysis,
  soldDate: Date
): IntercomEvent[] => {
  const isNewJob = (job.url ?? '').includes('new')
  const wasSold = savedJob?.sold ?? false
  const becameSold = job.sold && !wasSold

  const sharedMetadata = {
    customer: job.customer.displayName,
    property: job.property.displayName,
    estimated_price: centsToDisplay(analysis.estimated.price),
    estimated_hours: analysis.estimated.hours,
    salesperson: job.salesPersonName
  }

  if (job.type === ProjectTypes.Project) {
    const metadata: Record<string, unknown> = {
      ...sharedMetadata,
      name: job.name,
      total_workareas: job.workareas.length,
      status: job.status ? statusNames[job.status] : 'Unscheduled'
    }
    const events: IntercomEvent[] = []

    if (becameSold) {
      events.push({ name: 'sold-project', metadata: { ...metadata, sold_date: soldDate } })
    }
    if (job.status === ProjectStatuses.Completed && savedJob?.status !== ProjectStatuses.Completed) {
      events.push({
        name: 'completed-project',
        metadata: { ...metadata, final_price: centsToDisplay(analysis.final.price) }
      })
    }
    if (isNewJob) {
      events.push({ name: 'saved-new-project', metadata })
    }

    return events
  }

  const metadata: Record<string, unknown> = {
    ...sharedMetadata,
    total_tasks: job.workareas.length,
    final_price: centsToDisplay(analysis.final.price)
  }

  if (job.type === ProjectTypes.Maintenance) {
    const maintenanceMetadata = { ...metadata, active: job.active, num_of_years: 1 }
    const events: IntercomEvent[] = []

    if (isNewJob) {
      events.push({ name: 'saved-new-maintenance', metadata: maintenanceMetadata })
    }
    if (becameSold) {
      events.push({ name: 'sold-maintenance', metadata: { ...maintenanceMetadata, sold_date: soldDate } })
    }

    return events
  }

  return isNewJob ? [{ name: 'saved-new-ticket', metadata }] : []
}
